/**
 * Os ajustes do "Próximos dias" (a vista "Ajustes" do painel da previsão): o
 * que a variável no mapa mostra, a fumaça como MP2,5 ou AOD, a opacidade, as
 * rodadas em uso, as fontes e "Voltar ao observado". A variável se troca nos
 * botões da previsão do trilho (`BotoesDaPrevisao`). Com o acesso de pesquisa,
 * os cenários do WeatherNext 2, os termos do Google e "Sair do acesso".
 */
import { Fragment } from 'react';
import { ExternalLink, Undo2 } from 'lucide-react';
import { formatRelative } from '../lib/time';
import { useNow } from '../ui/hooks';
import { Hint, Section, Segmented, Slider } from '../ui/primitives';
import { SOBRE_MODELO_TEMPO, instanteDaRodada, modelosTempoNoIndice, type ModeloId } from './api';
import { SobreOAcesso } from './AcessoPesquisa';
import { type CenarioComo, type FumacaComo, type VariavelId } from './camadas';
import { definirModo, rodadaEmUso } from './motor';
import { LIMITES_OPACIDADE, usePrevisaoStore } from './store';

const ROTULOS: Record<VariavelId, string> = { chuva: 'Chuva', temperatura: 'Temperatura', vento: 'Vento', fumaca: 'Fumaça', cenarios: 'Cenários' };

const EXPLICA: Record<VariavelId, string> = {
  chuva: 'Chuva prevista em milímetros por hora (a média de cada intervalo de 3 h — de 6 h depois do 6º dia). Sem cor: menos de 0,1 mm/h.',
  temperatura: 'Temperatura do ar a 2 m do chão. A escala é fixa: a mesma cor é a mesma temperatura em qualquer dia.',
  vento: 'Velocidade do vento a 10 m do chão, em km/h.',
  fumaca: '',
  cenarios: '',
};

const EXPLICA_CENARIO: Record<CenarioComo, string> = {
  chance:
    'Dos 64 cenários do WeatherNext 2 (Google DeepMind), quantos têm pelo menos 1 mm de chuva nas 6 h que terminam no horário da barra. 70% = em 45 dos 64 cenários chove ali. Sem cor: menos de 10%.',
  forte:
    'Dos 64 cenários do WeatherNext 2 (Google DeepMind), quantos têm 10 mm ou mais de chuva nas 6 h que terminam no horário da barra — temporal. Sem cor: menos de 10%.',
};

const EXPLICA_FUMACA: Record<FumacaComo, string> = {
  pm25:
    'MP2,5 previsto na superfície (a parte da fumaça que entra fundo no pulmão), valor horário em µg/m³, nas faixas da Resolução CONAMA nº 491/2018. A classificação oficial usa a média de 24 h; um pico de uma hora não é a qualidade do ar do dia. Sem cor: "Boa" (até 25).',
  aod:
    'Profundidade óptica do aerossol de queimadas a 550 nm (matéria orgânica + carbono negro): quanto a fumaça da coluna inteira apaga a luz do sol, inclusive a fumaça alta, que não chega ao chão. Faixas iguais às do gráfico do ECMWF.',
};

function rotuloDaRodada(r: string): string {
  const d = new Date(instanteDaRodada(r));
  return `${String(d.getUTCHours()).padStart(2, '0')} UTC de ${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function AjustesDaPrevisao() {
  const variavel = usePrevisaoStore((s) => s.variavel);
  const fumaca = usePrevisaoStore((s) => s.fumaca);
  const cenario = usePrevisaoStore((s) => s.cenario);
  const opacidade = usePrevisaoStore((s) => s.opacidade);
  const indice = usePrevisaoStore((s) => s.indice);
  const rodadas = usePrevisaoStore((s) => s.rodadas);
  const situacao = usePrevisaoStore((s) => s.situacao);
  const exportando = usePrevisaoStore((s) => s.exportando);
  const { setFumaca, setCenario, setOpacidade } = usePrevisaoStore.getState();
  const now = useNow();

  const linhaModelo = (id: ModeloId, nome: string) => {
    const m = indice?.modelos[id];
    const r = rodadaEmUso(m, rodadas[id]);
    if (!m || !r) return <li>{nome}: sem rodada disponível{m?.erro ? ` (${m.erro.slice(0, 80)})` : ''}</li>;
    return (
      <li>
        {nome}: rodada das {rotuloDaRodada(r)} ({formatRelative(instanteDaRodada(r), now)}); próxima em ~12 h{id === 'wn2' ? ', ~10 h depois da hora da rodada' : ''}
      </li>
    );
  };

  return (
    <div>
      <Section title={`No mapa: ${ROTULOS[variavel].toLowerCase()}`}>
        {exportando && <Hint>Exportando a previsão: os ajustes ficam fixos até o vídeo terminar.</Hint>}
        <div className="space-y-3" inert={exportando}>
          <p className="text-xs leading-snug text-ink-300">
            {variavel === 'fumaca' ? EXPLICA_FUMACA[fumaca] : variavel === 'cenarios' ? EXPLICA_CENARIO[cenario] : EXPLICA[variavel]}
          </p>
          {variavel === 'cenarios' && (
            <Segmented
              label="Chance de"
              size="sm"
              value={cenario}
              options={[
                { value: 'chance', label: 'Chuva (≥ 1 mm)' },
                { value: 'forte', label: 'Chuva forte (≥ 10 mm)' },
              ]}
              onChange={setCenario}
            />
          )}
          {variavel === 'fumaca' && (
            <Segmented
              label="Fumaça como"
              size="sm"
              value={fumaca}
              options={[
                { value: 'pm25', label: 'MP2,5 (saúde)' },
                { value: 'aod', label: 'AOD (coluna)' },
              ]}
              onChange={setFumaca}
            />
          )}
          <Slider
            label="Opacidade"
            value={opacidade}
            min={LIMITES_OPACIDADE.min}
            max={LIMITES_OPACIDADE.max}
            step={LIMITES_OPACIDADE.step}
            onChange={setOpacidade}
            format={(v) => `${Math.round(v * 100)}%`}
          />
          <p className="text-xs leading-snug text-ink-300">
            As camadas observadas ficam desligadas, com o satélite do Google ao fundo, e voltam como estavam ao sair; para ligar alguma por cima
            da previsão, estenda “Observado” no trilho. Toque num ponto do mapa para ver a previsão dele.
          </p>
          <button
            type="button"
            onClick={() => definirModo(false)}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-ink-600 bg-ink-800 px-3 text-sm text-ink-100 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-violet-400 pointer-coarse:min-h-11"
          >
            <Undo2 className="size-4" aria-hidden />
            Voltar ao observado
          </button>
        </div>
      </Section>
      <Section title="Sobre a previsão" defaultOpen={false} storageKey="previsao-sobre">
        <div className="space-y-2 text-xs leading-snug text-ink-300">
          <p>
            <strong className="text-ink-100">Modelo, não medição.</strong> A previsão de tempo é de um modelo à escolha (IFS por padrão, 0,25° ≈ 28 km, até 15 dias); a de
            fumaça, do CAMS/Copernicus (0,4° ≈ 44 km, até 5 dias). Cada valor representa uma área do tamanho da célula, não um ponto. A partir do 8º
            dia, é tendência: a confiança cai.
          </p>
          {modelosTempoNoIndice(indice).length > 1 && (
            <div>
              <p>
                <strong className="text-ink-100">Modelos do tempo</strong>, trocados pelo botão “Modelo” (a fumaça é sempre do CAMS):
              </p>
              <ul className="mt-0.5 list-disc space-y-0.5 pl-4">
                {modelosTempoNoIndice(indice).map((m) => (
                  <li key={m}>
                    <strong className="text-ink-100">{SOBRE_MODELO_TEMPO[m].curto}</strong>: {SOBRE_MODELO_TEMPO[m].detalhe}.
                  </li>
                ))}
              </ul>
            </div>
          )}
          {situacao.estado === 'error' && !indice ? (
            <p>Previsão indisponível agora: {situacao.erro}</p>
          ) : (
            <ul className="list-disc space-y-0.5 pl-4">
              {linhaModelo('ifs', 'Tempo (ECMWF IFS)')}
              {modelosTempoNoIndice(indice)
                .filter((m) => m !== 'ifs')
                .map((m) => (
                  <Fragment key={m}>{linhaModelo(m, `Tempo (${SOBRE_MODELO_TEMPO[m].nome}${SOBRE_MODELO_TEMPO[m].ia ? ', IA' : ''})`)}</Fragment>
                ))}
              {linhaModelo('cams', 'Fumaça (CAMS)')}
              {indice?.modelos.wn2 && linhaModelo('wn2', 'Cenários (WeatherNext 2, acesso de pesquisa)')}
            </ul>
          )}
          <p>
            Fontes:{' '}
            <a className="inline-flex items-center gap-0.5 underline" href="https://www.ecmwf.int/en/forecasts/datasets/open-data" target="_blank" rel="noreferrer">
              ECMWF open data <ExternalLink className="size-3" aria-hidden />
            </a>{' '}
            (CC BY 4.0) ·{' '}
            <a className="inline-flex items-center gap-0.5 underline" href="https://atmosphere.copernicus.eu/" target="_blank" rel="noreferrer">
              CAMS <ExternalLink className="size-3" aria-hidden />
            </a>{' '}
            — Contains modified Copernicus Atmosphere Monitoring Service information.
          </p>
        </div>
      </Section>
      <SobreOAcesso />
    </div>
  );
}
